/**
 * JWT Utility Functions
 * Client-side only - never send secret to frontend
 */

export interface DecodedToken {
  userId: string;
  tenantId: string;
  email: string;
  role: string;
  iat: number;
  exp: number;
}

/**
 * Decode JWT token (no signature verification - done by backend)
 * @param token JWT token
 * @returns Decoded payload or null if invalid
 */
export function decodeToken(token: string): DecodedToken | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }

    // Decode payload (base64url)
    const payload = JSON.parse(atob(parts[1]));
    return payload as DecodedToken;
  } catch (error) {
    return null;
  }
}

/**
 * Check if token is expired
 * @param token JWT token
 * @returns true if expired, false if valid
 */
export function isTokenExpired(token: string): boolean {
  const decoded = decodeToken(token);
  if (!decoded) return true;

  const now = Math.floor(Date.now() / 1000);
  return decoded.exp < now;
}

/**
 * Check if token is still valid (not expired)
 * @param token JWT token
 * @returns true if valid, false if expired or invalid
 */
export function isTokenValid(token: string): boolean {
  return !isTokenExpired(token);
}

/**
 * Get expiration date of token
 * @param token JWT token
 * @returns Date object or null if invalid
 */
export function getTokenExpirationDate(token: string): Date | null {
  const decoded = decodeToken(token);
  if (!decoded) return null;

  return new Date(decoded.exp * 1000);
}

/**
 * Get time remaining until token expires (in seconds)
 * @param token JWT token
 * @returns Seconds remaining or -1 if already expired
 */
export function getTokenTimeRemaining(token: string): number {
  const decoded = decodeToken(token);
  if (!decoded) return -1;

  const now = Math.floor(Date.now() / 1000);
  return decoded.exp - now;
}
